export const ROUTE_LOCALE_KEYS = [
  "en-ca",
  "zh-cn",
  "zh-tw",
  "pa-in",
  "ko-kr",
  "fa-ir",
  "ar-sa",
] as const;

export type LocaleCode = (typeof ROUTE_LOCALE_KEYS)[number];

export type TextDirection = "ltr" | "rtl";

export type LocaleDefinition = {
  routeLocaleKey: LocaleCode;
  hrefLang: string;
  bcp47: string;
  labelEn: string;
  labelNative: string;
  dir: TextDirection;
  isDefault: boolean;
  formatLocale: string;
  translationStatus?: "needs-human-review";
};

/** Central locale registry — single source of truth for routing, SEO, and formatting. */
export const LOCALES: readonly LocaleDefinition[] = [
  {
    routeLocaleKey: "en-ca",
    hrefLang: "en-CA",
    bcp47: "en-CA",
    labelEn: "English (Canada)",
    labelNative: "English",
    dir: "ltr",
    isDefault: true,
    formatLocale: "en-CA",
  },
  {
    routeLocaleKey: "zh-cn",
    hrefLang: "zh-cn",
    bcp47: "zh-Hans",
    labelEn: "Simplified Chinese",
    labelNative: "简体中文",
    dir: "ltr",
    isDefault: false,
    formatLocale: "zh-CN",
  },
  {
    routeLocaleKey: "zh-tw",
    hrefLang: "zh-tw",
    bcp47: "zh-Hant",
    labelEn: "Traditional Chinese",
    labelNative: "繁體中文",
    dir: "ltr",
    isDefault: false,
    formatLocale: "zh-TW",
  },
  {
    routeLocaleKey: "pa-in",
    hrefLang: "pa-in",
    bcp47: "pa-IN",
    labelEn: "Punjabi",
    labelNative: "ਪੰਜਾਬੀ",
    dir: "ltr",
    isDefault: false,
    formatLocale: "pa-IN",
  },
  {
    routeLocaleKey: "ko-kr",
    hrefLang: "ko-kr",
    bcp47: "ko-KR",
    labelEn: "Korean",
    labelNative: "한국어",
    dir: "ltr",
    isDefault: false,
    formatLocale: "ko-KR",
  },
  {
    routeLocaleKey: "fa-ir",
    hrefLang: "fa-ir",
    bcp47: "fa-IR",
    labelEn: "Persian",
    labelNative: "فارسی",
    dir: "rtl",
    isDefault: false,
    formatLocale: "fa-IR",
  },
  {
    routeLocaleKey: "ar-sa",
    hrefLang: "ar-sa",
    bcp47: "ar-SA",
    labelEn: "Arabic",
    labelNative: "العربية",
    dir: "rtl",
    isDefault: false,
    formatLocale: "ar-SA",
  },
] as const;

export const DEFAULT_LOCALE: LocaleCode = "en-ca";

export const LOCALE_BY_KEY = Object.fromEntries(
  LOCALES.map((locale) => [locale.routeLocaleKey, locale]),
) as Record<LocaleCode, LocaleDefinition>;

/** Legacy URL locale segments → canonical route locale keys. */
export const LEGACY_LOCALE_SEGMENTS: Record<string, LocaleCode> = {
  "en-us": "en-ca",
  "zh-hans": "zh-cn",
  "zh-hant": "zh-tw",
  pa: "pa-in",
  ko: "ko-kr",
  fa: "fa-ir",
  ar: "ar-sa",
};

export function isLocaleCode(value: string): value is LocaleCode {
  return (ROUTE_LOCALE_KEYS as readonly string[]).includes(value);
}

export function localeDir(locale: LocaleCode): TextDirection {
  return LOCALE_BY_KEY[locale].dir;
}

export function localeHrefLang(locale: LocaleCode): string {
  return LOCALE_BY_KEY[locale].hrefLang;
}

export function localeBcp47(locale: LocaleCode): string {
  return LOCALE_BY_KEY[locale].bcp47;
}
